
import BackgroundJobs from '@/components/BackgroundJobs.vue';
import Button from '@/components/buttons/Button.vue';
import QuickClockIn from '@/components/clockIn/QuickClockIn.vue';
import LockedFeatureDialog from '@/components/dialog/LockedFeatureDialog.vue';
import ToolTipWrapper from '@/components/info/ToolTipWrapper.vue';
import BubbleIcon from '@/components/interface/BubbleIcon.vue';
import CompanyTime from '@/components/interface/CompanyTime.vue';
import { checkBillingContact } from '@/lib/billing/billing';
import {
  countEmployees,
  getEmployeeTierPriceInPounds,
  getPackagePricesByType,
  getPackageQuota,
  getSubscriptionPriceByProductLookupKey,
} from '@/lib/billing/billingFunctions';
import { CurrentEmployeeStatuses } from '@/lib/employee/employeeFunctions';
import { EventsEnum } from '@/lib/enum/events';
import { Icon } from '@/lib/enum/Icon';
import {
  companyCanAccess,
  Feature,
} from '@/lib/permission/companyAccessFeatures';
import { userCan } from '@/lib/permission/userCan';
import { Entity } from '@/lib/realtime/RealtimeEntity';
import { realtimeQuery } from '@/lib/realtime/weak/realtimeFunctions';
import { routes } from '@/router/routes';
import store from '@/store';
import SendMessageDialog from '@/views/messaging/SendMessageDialog.vue';
import spacetime from 'spacetime';
import {
  BillingPackage,
  BillingProductFeaturesEnum,
  BillingProductTypeEnum,
  BillingQuotaDefinitionLookupKeyEnum,
} from '../../../api/v1';

export default {
  name: 'Topbar',

  components: {
    CompanyTime,
    LockedFeatureDialog,
    SendMessageDialog,
    Button,
    ToolTipWrapper,
    BubbleIcon,
    QuickClockIn,
    BackgroundJobs,
  },

  data() {
    return {
      EventsEnum,
      routes,
      userCan,
      Icon,
      showSendMessageDialog: false,
      lockedFeatureDialog: null as BillingProductFeaturesEnum,
      billingPackages: realtimeQuery(Entity.BillingPackage).fetch(),
      employees: realtimeQuery(Entity.Employee)
        .whereIn('status', CurrentEmployeeStatuses)
        .fetch(),
    };
  },

  computed: {
    warnings: () => store.state.appWarnings.warning,
    isImpersonationSession: () => store.state.isImpersonationSession,

    currentSubscription: () => store.state.billing.currentSubscription,
    currentCompany: () => store.getters.currentCompany,
    loggedInEmployee: () => store.getters.loggedInEmployee,
    isTrialVersion: () => store.getters['billing/isTrialVersion'],
    trialDaysLeft: () => store.getters['billing/trialDaysLeft'],
    nextRenewalDate: () => store.getters['billing/nextRenewalDate'],
    isUpgradable: () => store.getters['billing/isUpgradable'],
    billingOverview: () => store.state.billing.billingOverview,
    currentPackage: () => store.getters['billing/currentPackage'],

    availableBillingPackages(): Array<BillingPackage> {
      return this.billingPackages.data.filter(
        (p) => p.id !== this.currentPackage.id && p.allowsSignups === true,
      );
    },

    hasMissingBillingData(): boolean {
      /** Requires billing if:
       * - Package costs more than 0
       * - Needs to pay for employees
       *   - Over soft limit and fixed rate is more than 0
       *   - Tiered employee cost for current count is more than 0
       * */

      const packagePrice =
        (getPackagePricesByType(
          this.currentPackage,
          BillingProductTypeEnum.Plan,
        )[0]?.fixedPrice || 0) / 100;

      const employeePrice = (() => {
        const price = getSubscriptionPriceByProductLookupKey(
          this.currentSubscription,
          'employees',
        );

        const employeeCount = countEmployees(this.employees.data);

        if (price.fixedPrice) {
          const employeeSoftLimit = getPackageQuota(
            this.currentPackage,
            BillingQuotaDefinitionLookupKeyEnum.EmployeeSoftLimit,
          );

          return employeeCount > employeeSoftLimit ? price.fixedPrice : 0;
        }

        return getEmployeeTierPriceInPounds(price, employeeCount);
      })();

      const requiresBillingDetails = packagePrice > 0 || employeePrice > 0;

      return (
        requiresBillingDetails &&
        this.billingOverview &&
        (!this.billingOverview.hasPaymentMethods ||
          !this.billingOverview.hasDetails)
      );
    },

    billingButtonPretextNumber(): number {
      if (this.isTrialVersion) {
        return this.trialDaysLeft;
      }

      return spacetime
        .now(this.currentCompany.timezone)
        .diff(spacetime(this.nextRenewalDate), 'days');
    },
  },

  async mounted() {
    await checkBillingContact();
  },

  methods: {
    async upgrade() {
      this.$root.$emit(EventsEnum.ToggleBillingDialog);
    },

    openMessageDialog() {
      if (companyCanAccess(Feature.TeamMessaging)) {
        this.showSendMessageDialog = true;
        return;
      }
      this.lockedFeatureDialog = Feature.TeamMessaging;
    },
  },
};
